import {
	JiraExtension,
	type ForgeUIExtensionType,
	type JiraExtensionType,
} from '@atlassian/forge-ui-types';
import { createMakeProductURLPath } from '../createMakeProductURLPath';
import { ensurePropertyExists } from './common';

type AdminPageProperties = {
	useAsConfig?: boolean;
	useAsGetStarted?: boolean;
};

type FullForgeUIExtensionType = ForgeUIExtensionType & {
	properties: ForgeUIExtensionType['properties'];
};

const makeJiraURLPath = createMakeProductURLPath('/jira');

const PROJECT_TYPE_SEGMENT_MAP: Record<string, string> = {
	software: 'software',
	business: 'core',
	service_desk: 'servicedesk',
};

const projectTypeSegmentCache = new Map<string, string>();

export const clearProjectTypeCache = (): void => projectTypeSegmentCache.clear();

const resolveJiraProjectTypeSegment = async (projectKey: string): Promise<string> => {
	const cached = projectTypeSegmentCache.get(projectKey);
	if (cached) {
		return cached;
	}
	try {
		const resp = await fetch(`/rest/api/3/project/${projectKey}`);
		const { projectTypeKey } = await resp.json();
		const segment = PROJECT_TYPE_SEGMENT_MAP[projectTypeKey];
		if (segment) {
			projectTypeSegmentCache.set(projectKey, segment);
			return segment;
		}
		// eslint-disable-next-line no-console
		console.warn(`Navigation: Unknown project type key "${projectTypeKey}" for "${projectKey}"`);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn(`Navigation: Failed to resolve project type for "${projectKey}"`, e);
	}
	// default to software segment if we fail to resolve the project type, this ensure backwards compatibility with the existing apps
	return 'software';
};

// NOTE: Project Keys are always uppercase in Jira - the urls below will automatically convert any provided project key to uppercase
// https://confluence.atlassian.com/adminjiraserver/changing-the-project-key-format-938847081.html#:~:text=The%20project%20key%20only%20supports,lowercase%20characters%20to%20uppercase%20ones.

export const jiraExtensionURLHelpers: Partial<
	Record<
		JiraExtensionType,
		(extension: FullForgeUIExtensionType, params?: any) => string | null | Promise<string | null>
	>
> = {
	[JiraExtension.FULL_PAGE]: ({ appId, environmentId, properties }) => {
		const { routePrefix } = properties;
		if (!routePrefix) {
			return null;
		}
		return `/forge-apps/a/${appId}/e/${environmentId}/r/${routePrefix}`;
	},
	[JiraExtension.ADMIN_PAGE]: ({ appId, environmentId, properties }) => {
		const { useAsGetStarted, useAsConfig } = properties as AdminPageProperties;
		if (useAsGetStarted) {
			return makeJiraURLPath(`/settings/apps/get-started/${appId}/${environmentId}`);
		}
		if (useAsConfig) {
			return makeJiraURLPath(`/settings/apps/configure/${appId}/${environmentId}`);
		}
		return makeJiraURLPath(`/settings/apps/${appId}/${environmentId}`);
	},
	[JiraExtension.GLOBAL_PAGE]: ({ appId, environmentId }) => {
		return makeJiraURLPath(`/apps/${appId}/${environmentId}`);
	},
	[JiraExtension.PERSONAL_SETTINGS_PAGE]: ({ appId, environmentId }) => {
		return makeJiraURLPath(`/settings/personal/apps/${appId}/${environmentId}`);
	},
	[JiraExtension.PROJECT_SETTINGS_PAGE]: async (
		{ appId, environmentId }: FullForgeUIExtensionType,
		{ projectKey }: { projectKey: string },
	) => {
		const validatedKey = ensurePropertyExists('projectKey', projectKey).toUpperCase();
		const segment = await resolveJiraProjectTypeSegment(validatedKey);
		return makeJiraURLPath(
			`/${segment}/projects/${validatedKey}/settings/apps/${appId}/${environmentId}`,
		);
	},
	[JiraExtension.PROJECT_PAGE]: async (
		{ appId, environmentId }: FullForgeUIExtensionType,
		{ projectKey }: { projectKey: string },
	) => {
		const validatedKey = ensurePropertyExists('projectKey', projectKey).toUpperCase();
		const segment = await resolveJiraProjectTypeSegment(validatedKey);
		return makeJiraURLPath(`/${segment}/projects/${validatedKey}/apps/${appId}/${environmentId}`);
	},
};

export const jiraNavigationTargetHandlers = {
	dashboard: async ({ dashboardId }: { dashboardId: string }): Promise<string> => {
		return makeJiraURLPath(`/dashboards/${ensurePropertyExists('dashboardId', dashboardId)}`);
	},
	issue: async ({ issueKey }: { issueKey: string }): Promise<string> => {
		return `/browse/${ensurePropertyExists('issueKey', issueKey)}`;
	},
	// equivalent to Connects projectAdminSummary target
	projectSettingsDetails: async ({ projectKey }: { projectKey: string }): Promise<string> => {
		const validatedKey = ensurePropertyExists('projectKey', projectKey).toUpperCase();
		const segment = await resolveJiraProjectTypeSegment(validatedKey);
		return makeJiraURLPath(`/${segment}/projects/${validatedKey}/settings/details`);
	},
} as const;
